// Platform detector — single source of truth for "where is this code running?"
//
// The desktop/web codebase used to import `Capacitor` directly from
// `@capacitor/core` to call `isNativePlatform()`. That's wrong here for two
// reasons:
//   1. `@capacitor/core` isn't a dependency of the desktop project at all,
//      so a static import would break the build.
//   2. We have three runtime targets now (iOS native, Electron desktop, plain
//      browser) and need a single function that distinguishes all of them.
//
// Every place in the React code that asks "am I native?" or "am I in
// Electron?" should import from this module instead of touching Capacitor or
// `window.consistency` directly. That way when we add the iOS shell back
// later (sharing this codebase with the App Store version), there's exactly
// one place to teach about the new environment.

/**
 * Runtime platform. Returns one of:
 *   - 'desktop' : Running inside our Electron shell (window.consistency exists)
 *   - 'ios'     : Running inside a Capacitor iOS shell (legacy / cross-build)
 *   - 'web'     : Plain browser (Vercel deployment, mobile Safari, etc.)
 *
 * Detection order matters. We check Electron first because it sets a marker
 * via preload (window.consistency.isElectron). Capacitor is checked second
 * by feature-detecting its global; this codepath is dead in the current
 * desktop project (no @capacitor/core installed) but stays here so the
 * helper is useful if we ever reunify with the iOS app.
 */
export function getPlatform() {
  if (typeof window === 'undefined') return 'web';
  if (window.consistency?.isElectron) return 'desktop';
  // Capacitor exposes `window.Capacitor` when running inside a native shell.
  // We don't import @capacitor/core; we just feature-detect.
  const cap = /** @type {any} */ (window).Capacitor;
  if (cap && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform()) {
    return 'ios';
  }
  return 'web';
}

/** True on iOS native or Electron desktop — i.e. anywhere we're not "just a webpage". */
export function isNative() {
  const p = getPlatform();
  return p === 'desktop' || p === 'ios';
}

/** True only inside our Electron desktop shell. */
export function isDesktop() {
  return getPlatform() === 'desktop';
}

/** True only inside the iOS Capacitor shell. */
export function isIOS() {
  return getPlatform() === 'ios';
}

/** True in a plain browser tab — Vercel, localhost dev, mobile Safari. */
export function isWeb() {
  return getPlatform() === 'web';
}

/**
 * Open a URL in the user's default browser (Electron) or just navigate
 * (web). Use this for Stripe Checkout redirects, Terms/Privacy links,
 * billing-portal deep links, and any other off-app link.
 *
 * On Electron, window.consistency.openExternal is wired through preload.cjs.
 * On the web, we fall back to opening a new tab so we don't lose the user's
 * place in the app.
 */
export function openExternal(url) {
  if (typeof url !== 'string' || !/^https?:\/\//i.test(url)) return;
  try {
    if (isDesktop() && typeof window.consistency?.openExternal === 'function') {
      window.consistency.openExternal(url);
      return;
    }
  } catch { /* fall through to web behavior */ }
  if (typeof window !== 'undefined') {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

/**
 * The absolute base URL of our Vercel API.
 *
 * In the iOS shell and the Electron shell, fetch('/api/foo') doesn't work
 * because the page is loaded from `capacitor://` or `file://` — there's no
 * server at that origin. We always need to point at the deployed Vercel
 * project. On the open web, relative paths work because Vercel serves both
 * the static SPA and the /api/* serverless functions from the same origin,
 * so we keep it relative to avoid CORS preflight.
 *
 * Override at build time with VITE_API_BASE if you deploy the web app to a
 * different domain than the API (e.g. app.proxera.io serving the SPA but
 * api.proxera.io serving the functions).
 */
// New project domain — the desktop + web SPA is hosted on consistency.proxera.io
// (custom domain pointing at the proxera-consistency-desktop Vercel project),
// which also hosts the /api/* serverless functions. The iOS app still points
// at proxera-consistency.vercel.app and has its own RevenueCat webhook there;
// the two deployments are intentionally separate so a desktop-side change
// (Stripe webhook, new endpoint, etc.) can't accidentally break iOS in review.
const DEFAULT_API_BASE = 'https://consistency.proxera.io';
const API_BASE = (
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE) ||
  DEFAULT_API_BASE
);

export function apiUrl(path) {
  if (!path.startsWith('/')) path = '/' + path;
  return isNative() ? `${API_BASE}${path}` : path;
}
